import {
  CheckCircleIcon,
  ChefHatIcon,
  Icon,
  PackageIcon,
  TimerIcon,
  XCircleIcon,
} from '@phosphor-icons/react'
import { TFunction } from 'i18next'
import { z } from 'zod'

import { DayNameNarrow } from '@/constants'
import { PersonDisplay } from '@/models/features/display-column.models'

export interface MarketShopProductModel {
  createdAt: number
  updatedAt: number
  id: number
  summary: string
  description: string
  sku: string
  price: number
  discountedPrice?: number
  quantity: number
  image: string
  tax: number
  marketShopId: number
  categoryId: number
  tags: MarketShopInventoryTag[]
  unlimited: boolean
}

export const MarketShopInventoryItemStatusEnum = z.enum(['active', 'hidden', 'outOfStock'])
export type MarketShopInventoryItemStatus = z.infer<typeof MarketShopInventoryItemStatusEnum>

export interface MarketShopInventoryCategory {
  id: number
  name: string
  position: number
  marketShopId: number
  createdAt: number
  updatedAt: number
}

export interface MarketShopInventoryTag {
  id: number
  name: 'Discount' | 'NEW'
  discount?: number
}

export interface MarketShopInventoryItem {
  id: number
  summary: string
  description: string
  sku: string
  price: number
  quantity: number
  image: string
  tax: number
  purchased: boolean
  hidden: boolean
  unlimited: boolean
  marketShopId: number
  categoryId?: {
    id: number
    name: string
    position: number
  }
  tags: MarketShopInventoryTag[]
  originalPrice: number
  discountedPrice: number
}

export type MarketShopDeliveryType = 'delivery' | 'pickup'

export type MarketShopMobilePaymentMethod = 'card' | 'cash'

export const MarketShopOrderStatusEnum = z.enum([
  'new',
  'in_progress',
  'awaiting_pickup',
  'completed',
  'declined',
])

export type MarketShopOrderStatus = z.infer<typeof MarketShopOrderStatusEnum>

export type MarketShopOrderStatusData = {
  label: string
  icon: Icon
  validTransitions: MarketShopOrderStatus[]
}

export const marketShopOrderStatusDataMap: Record<
  MarketShopOrderStatus,
  MarketShopOrderStatusData
> = {
  awaiting_pickup: {
    icon: PackageIcon,
    label: 'Awaiting Pickup',
    validTransitions: ['in_progress', 'completed'],
  },
  completed: {
    icon: CheckCircleIcon,
    label: 'Completed',
    validTransitions: ['in_progress', 'completed'],
  },
  declined: {
    icon: XCircleIcon,
    label: 'Declined',
    validTransitions: ['new'],
  },
  in_progress: {
    icon: ChefHatIcon,
    label: 'In Progress',
    validTransitions: ['new', 'awaiting_pickup'],
  },
  new: {
    icon: TimerIcon,
    label: 'New',
    validTransitions: ['in_progress', 'declined'],
  },
}

export type MarketShopOrderItem = {
  id: number
  orderId: number
  productId: MarketShopProductModel
  quantity: number
  unitPrice: number
  taxRate: number
  subTotal: number
  taxAmount: number
  note?: string
  createdAt: number
  updatedAt: number
}

export type MarketShopResidentModel = {
  apartment: string
  fullName: string
}

export type MarketShopOrderModel = {
  id: number
  marketShopId: number
  clientId?: number
  totalAmount: number
  sessionId: string
  deliveryType: MarketShopDeliveryType
  paymentMethod: MarketShopMobilePaymentMethod
  orderNotes?: string
  createdAt: number
  updatedAt: number
  resident?: MarketShopResidentModel
  items: MarketShopOrderItem[]
  status: MarketShopOrderStatus
}

export interface MarketShopOrderCartItem {
  summary: string
  quantity: number
}

export interface MarketShopTransactionModel {
  // Note: the BE's per-column person details - only the fields enabled in its settings (EL-15064)
  residentDisplay?: PersonDisplay
  id: number
  transactionId: string
  orderId: number
  paymentMethod: string
  completedAt: number
  orderStatus: MarketShopOrderStatus
  orderCart: MarketShopOrderCartItem[]
  orderSummary: string
  subTotal: number
  discount: number
  tax: number
  total: number
  residentName?: string
  // Note: the ordering resident (restaurant orders only) - the "Resident" person column's details
  residentFirstName?: string | null
  residentLastName?: string | null
  residentAvatar?: string | null
  residentEmail?: string | null
  residentApartment?: string | null
  residentPhone?: string | null
  residentBuildingNumber?: string | null
  residentStreet?: string | null
  // Note: not sent yet - the name links to the resident's profile once the BE adds it
  clientId?: number | null
  deliveryType?: MarketShopDeliveryType
}

export interface MarketShopBusinessHoursModel {
  name: DayNameNarrow
  startTime: number
  endTime: number
}

export type MarketShopCategoryModel = {
  id: number
  name: string
  position: number
}

export interface MarketShopSettingsModel {
  id: number
  createdAt: number
  updatedAt: number
  marketShopId: number
  deliverToMe: boolean
  tipsEnabled: boolean
  enablePayByCard: boolean
  enablePayByCash: boolean
  tips: number[]
  displayNameForCashPayment: string
  businessHours: MarketShopBusinessHoursModel[]
  globalTax: number
  categories: MarketShopCategoryModel[]
}

export interface MarketShopClientOrderModel {
  id: number
  status: MarketShopOrderStatus
  deliveryType: MarketShopDeliveryType
  paymentMethod: MarketShopMobilePaymentMethod
  orderNotes: string | null
  products: Array<{
    summary: string
    quantity: number
    subTotal: number
    tax: number
    note?: string
  }>
  subTotal: number
  tax: number
  discount: number
  tipAmount: number
  total: number
  updatedAt: number
}

export const getOrderStatusToBadgeLabelMap = (
  t: TFunction<['market-shop']>,
): Record<MarketShopOrderStatus, (order: MarketShopClientOrderModel) => string> => ({
  awaiting_pickup: ({ deliveryType }) =>
    deliveryType === 'delivery'
      ? t('market-shop:orderStatusBadge.delivery')
      : t('market-shop:orderStatusBadge.pickup'),
  completed: () => t('market-shop:orderStatusBadge.completed'),
  declined: () => t('market-shop:orderStatusBadge.declined'),
  in_progress: () => t('market-shop:orderStatusBadge.inProgress'),
  new: () => t('market-shop:orderStatusBadge.new'),
})

export const MARKET_SHOP_MIN_TIP_AMOUNT = 1

export const MARKET_SHOP_MAX_TIP_AMOUNT = 100
